import { useStoreActions } from 'easy-peasy';
import { PropTypes } from 'prop-types';
import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { DataLoader } from '@ge/components/data-loader';
import { Icon, Icons } from '@ge/components/icon';
import { useColumnState } from '@ge/components/table/use-column-state';
import AddDamageDialog from '@ge/feat-monitor/components/add-damage-dialog';
import {
  Capability,
  DataLoaderType,
  EntityType,
  MonitorDefs,
  SortDirection,
} from '@ge/models/constants';
import { AuthRender } from '@ge/shared/components/auth-render';
import { CaseUnlinkDialog } from '@ge/shared/components/cases/case-unlink-dialog';
import CaseAlertsTable from '@ge/shared/components/tables/case-alerts-table';
import CaseDamagesTable from '@ge/shared/components/tables/case-damages-table';
import CaseOverviewTable from '@ge/shared/components/tables/case-overview-table';
// import CaseRecentActivityTable from '@ge/shared/components/tables/case-recent-activity-table';
import { useCaseLinkedTaskIds } from '@ge/shared/components/use-linked-task-menu';
import { EntityDetailsContext } from '@ge/shared/context/entity-details-context';
import {
  CaseDamagesColumnDefs,
  defaultCaseDamagesCols,
} from '@ge/shared/models/table-col-defs/case-damage-table-cols';
import { killEventPropagation } from '@ge/shared/util/general';

import { useInThisCase } from '../../../hooks/use-in-this-case';
import TabDetailHeader from '../components/tab-detail-header';
import { TabDetailContainer, EntityCollapsiblePanel } from '../entity-details-shared';

import { CaseDescription } from './case-description';
import { useCaseDamagesTable } from './data-hooks/use-case-damages-table';

const SectionContainer = styled.div`
  position: relative;
  display: flex;
  overflow-x: auto;
  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-track {
    background: ${(props) => props.theme.scrollbar.trackBackground};
  }
  &::-webkit-scrollbar-thumb {
    background: ${(props) => props.theme.scrollbar.thumbBackground};
    border-radius: 2.5px;
  }
  table tbody tr:first-of-type td.group-last {
    top: 0px;
  }
  td.group-last {
    background: ${(props) => props.theme.entityDetails.cases.details.casePanelTableBackground};
  }
  th.group-last {
    span {
      margin-right: 8px;
    }
  }
`;

const AlertsEmpty = styled.div`
  margin-left: 21px;
  margin-top: 12px;
  p {
    font-size: 14px;
    line-height: 21px;
    margin: 0;
  }
`;

const ButtonWrapper = styled.span`
  display: flex;
  align-items: self-start;
  gap: 10px;
`;

const NewDamageButton = styled.button`
  font-weight: 600;
  color: ${(props) => props.theme.malAdjustmentTable.approveRejectSelectionSection.btntextColor};
  font-size: 11px;
  letter-spacing: 0;
  padding: 0;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const AddIcon = styled(Icon).attrs((props) => ({
  size: 12,
  color: props.theme.dataExplorer.header.addIconColor,
}))`
  flex-shrink: 0;
`;

const TableSelectionBar = styled.div`
  height: 50px;
  background: ${(props) => props.theme.caseDamages.selectionBgColor};
  border-bottom: 3px solid ${(props) => props.theme.caseDamages.selectionBorderColor};
  display: flex;
  align-items: center;
  padding-left: 24px;
  font-size: 12px;
  font-weight: 600;
  line-height: 14.4px;
  margin-top: 10px;
`;

const SeperatorWrapper = styled.span`
  box-sizing: border-box;
  height: 22px;
  width: 1px;
  border: 1px solid ${(props) => props.theme.caseDamages.seperatorColor};
  margin-left: 15px;
`;

const DetachButton = styled.button`
  background: ${(props) => props.theme.caseDamages.detachBtnBgColor};
  border: 1px solid ${(props) => props.theme.caseDamages.detachBtnBorderColor};
  font-size: 13px;
  font-weight: 400;
  line-height: 15.6px;
  color: ${(props) => props.theme.caseDamages.textColor};
  height: 24px;
  min-width: 80px;
  border-radius: 4px;
  margin-left: 15px;
`;

const CloseButtonWrapper = styled.span`
  margin-left: auto;
  margin-right: 15px;
`;

const CloseIcon = styled(Icon).attrs((props) => ({
  size: 10,
  icon: Icons.CLOSE,
  color: props.color ? props.color : props.theme.entityDetails.headerIconColor,
}))``;

/**
 * Available sort metrics for Case Damage.
 */
export const CaseDamageMetric = {
  SELECTED: 'selected',
};

export const CaseOverview = ({ caseDetail, handleDetailClick, isCaseDetailsLoading }) => {
  const { t } = useTranslation(['entity-details']);

  const [selectedCases, setSelectedCases] = useState([]);

  const [triggerHeight, setTriggerHeight] = useState(false);
  const [dataToUnlink, setDataToUnlink] = useState({});
  const [showAddDamageDialog, setShowAddDamageDialog] = useState(false);
  const [currentSearch, setCurrentSearch] = useState({});
  const [filterDefs, setFilterDefs] = useState({});
  const [checkedCaseDamages, setCheckedCaseDamages] = useState(0);
  const [isCloseTriggered, setIsCloseTriggered] = useState(false);
  const [showDetachModal, setShowDetachModal] = useState(false);
  const [isCaseDamageIdsChanged, setIsCaseDamageIdsChanges] = useState(false);
  const damages = caseDetail?.damages ?? [].join();
  const [damagesIds, setDamagesIds] = useState([]);

  const { showTaskDetails } = useContext(EntityDetailsContext);

  const { updateCases } = useStoreActions((actions) => actions.cases);

  const { childCasesAndTasks, isLoading: isInThisCaseLoading } = useInThisCase({ caseDetail });

  const { taskIds: caseTaskIds } = useCaseLinkedTaskIds({ caseId: caseDetail.id });

  const { sortDirection, sortMetric, updateSortMetric } = useColumnState({
    columnDefs: CaseDamagesColumnDefs,
    defaultCols: defaultCaseDamagesCols,
    defaultSortMetric: CaseDamageMetric.SELECTED,
    defaultSortDirection: SortDirection.ASC,
    sortStateId: MonitorDefs.CASE_DAMAGES_FILTER_STATE_ID,
  });

  useEffect(() => {
    const childCaseIds = caseDetail?.childCaseIds || [];
    if (!childCaseIds) return;
    const extractedDamages = [];
    childCaseIds.forEach((childId) => {
      caseDetail?.childCases?.forEach((e) => {
        if (e?.id === childId) {
          e?.damages?.forEach((item) => {
            extractedDamages.push(item?.damageId);
          });
        }
      });
    });
    setDamagesIds(extractedDamages);
  }, [caseDetail]);

  const {
    data: caseDamages,
    isLoading: isCaseDamagesLoading,
    filterValues,
  } = useCaseDamagesTable({
    filters: filterDefs,
    search: currentSearch,
    sortMetric,
    sortDirection,
    damages,
    isCaseDamageIdsChanged,
    setIsCaseDamageIdsChanges,
    damagesIds,
  });

  useEffect(() => {
    if (!isCaseDetailsLoading) {
      setSelectedCases([...caseDetail.childCaseIds, ...caseTaskIds]);
    }
  }, [caseDetail.childCaseIds, caseTaskIds, isCaseDetailsLoading]);

  useEffect(() => {
    setTriggerHeight((_triggerHeight) => !_triggerHeight);
  }, [isCaseDetailsLoading, caseDetail, selectedCases, isInThisCaseLoading]);

  useEffect(() => {
    damages.length ? setIsCaseDamageIdsChanges(true) : setCheckedCaseDamages(0);
  }, [damages]);

  const sortByMostRecent = (a, b) => new Date(b.startDate) - new Date(a.startDate);

  const selectedAlerts = useMemo(() => {
    const alerts = !caseDetail.childCaseIds?.length
      ? caseDetail.alerts
      : caseDetail.alerts.filter((a) => selectedCases.includes(a.caseId));
    // renaming to startDate and endDate to fix paddingTop in CaseAlertsTable calculated in DynamicTable
    return alerts
      ?.map(({ start, end, ...rest }) => ({ startDate: start, endDate: end, ...rest }))
      .sort(sortByMostRecent);
  }, [caseDetail, selectedCases]);

  const toggleIncludedCase = useCallback((selectedCaseId) => {
    setSelectedCases((_selectedCases) =>
      _selectedCases.includes(selectedCaseId)
        ? _selectedCases.filter((caseId) => caseId !== selectedCaseId)
        : [..._selectedCases, selectedCaseId],
    );
  }, []);

  const handleTaskSelect = useCallback(
    (e, id, type) => {
      killEventPropagation(e);
      if (type === EntityType.TASK) showTaskDetails(id);
    },
    [showTaskDetails],
  );

  const handleUnlinkSelect = (entity) => {
    setDataToUnlink(entity);
  };

  const handleUnlinkClose = () => {
    setDataToUnlink({});
  };

  const handleUnlinkSave = (res) => {
    if (res.cases?.length > 0) {
      updateCases(res.cases);
    }
    setDataToUnlink({});
  };

  const handleFilter = useCallback(
    (updateDefs) => {
      setFilterDefs(updateDefs);
    },
    [setFilterDefs],
  );

  const handleFilterChange = useCallback((search) => setCurrentSearch(search), [setCurrentSearch]);

  if (!caseDetail) {
    return null;
  }

  return (
    <TabDetailContainer>
      <TabDetailHeader
        entity={caseDetail}
        entityType={EntityType.CASE}
        isLoading={isCaseDetailsLoading}
      />
      <CaseDescription caseDetails={caseDetail} isLoading={isCaseDetailsLoading} />
      <EntityCollapsiblePanel
        headerContent={<h2>{t('case_panel.in_this_case', 'In This Case')}:</h2>}
        expanded={true}
        triggerHeight={triggerHeight}
      >
        <SectionContainer>
          <DataLoader
            type={DataLoaderType.TABLE}
            isLoading={isCaseDetailsLoading || isInThisCaseLoading}
          >
            <CaseOverviewTable
              childCases={childCasesAndTasks}
              checked={selectedCases}
              toggleIncludedRow={toggleIncludedCase}
              onTaskSelect={handleTaskSelect}
              onUnlinkSelect={handleUnlinkSelect}
            />
          </DataLoader>
        </SectionContainer>
      </EntityCollapsiblePanel>
      <AuthRender capability={Capability.INTERNAL_ACCESS} edit siteLevel={false}>
        <EntityCollapsiblePanel
          headerContent={
            <HeaderContent>
              <h2>{t('case_panel."evidence":', 'Evidence')}</h2>
              <ButtonWrapper>
                <AddIcon icon={Icons.ADD} />
                <NewDamageButton onClick={() => setShowAddDamageDialog(true)}>
                  {t('case_panel."new-damage"', 'NEW DAMAGE')}
                </NewDamageButton>
              </ButtonWrapper>
            </HeaderContent>
          }
          expanded={!showAddDamageDialog}
          triggerHeight={triggerHeight}
          allowOverflow={true}
        >
          <DataLoader
            type={DataLoaderType.TABLE}
            isLoading={isCaseDetailsLoading || isCaseDamagesLoading}
          >
            {checkedCaseDamages ? (
              <TableSelectionBar>
                <span>
                  {checkedCaseDamages} {t('case_damages.selected', 'Selected')}
                </span>
                <SeperatorWrapper />
                <DetachButton onClick={() => setShowDetachModal(true)}>
                  {t('case_damages.detach', 'DETACH')}
                </DetachButton>
                <CloseButtonWrapper>
                  <button type="button" onClick={() => setIsCloseTriggered(true)}>
                    <CloseIcon />
                  </button>
                </CloseButtonWrapper>
              </TableSelectionBar>
            ) : null}
            <SectionContainer>
              {!isCaseDetailsLoading && !selectedAlerts?.length ? (
                damages?.length ? (
                  <CaseDamagesTable
                    caseDamages={caseDamages}
                    sortAction={updateSortMetric}
                    sortMetric={sortMetric}
                    sortDirection={sortDirection}
                    onFilter={handleFilter}
                    onFilterChange={handleFilterChange}
                    filterValues={filterValues}
                    setCheckedCaseDamages={setCheckedCaseDamages}
                    isCloseTriggered={isCloseTriggered}
                    setIsCloseTriggered={setIsCloseTriggered}
                    showDetachModal={showDetachModal}
                    setShowDetachModal={setShowDetachModal}
                    caseId={caseDetail?.id}
                    caseDetail={caseDetail}
                    isAssetPanel={false}
                  />
                ) : (
                  <AlertsEmpty>
                    <p>{t('case_panel.no_evidence', 'No Evidence Attached To This Case')}</p>
                  </AlertsEmpty>
                )
              ) : (
                <CaseAlertsTable alerts={selectedAlerts} handleDetailClick={handleDetailClick} />
              )}
            </SectionContainer>
          </DataLoader>
        </EntityCollapsiblePanel>
      </AuthRender>
      {/* As per Product, commenting for now */}
      {/* <EntityCollapsiblePanel
        headerContent={<h2>{t('case_panel.recent_activity', 'Recent Activity')}</h2>}
        expanded={true}
        triggerHeight={triggerHeight}
      >
        <DataLoader type={DataLoaderType.TABLE} isLoading={isCaseDetailsLoading}>
          <SectionContainer>
            <CaseRecentActivityTable activity={[]} />
          </SectionContainer>
        </DataLoader>
      </EntityCollapsiblePanel> */}
      <CaseUnlinkDialog
        caseDetail={caseDetail}
        isOpen={Boolean(dataToUnlink.id)}
        entityId={dataToUnlink.id}
        entityType={dataToUnlink.type}
        onClose={handleUnlinkClose}
        onSave={handleUnlinkSave}
      />
      {showAddDamageDialog && selectedCases ? (
        <AddDamageDialog
          caseDetail={caseDetail}
          entityType={EntityType.CASE}
          onClose={(e) => {
            killEventPropagation(e);
            setShowAddDamageDialog(false);
          }}
          isUpdate={false}
        />
      ) : null}
    </TabDetailContainer>
  );
};

CaseOverview.propTypes = {
  caseDetail: PropTypes.instanceOf(Object).isRequired,
  handleDetailClick: PropTypes.func,
  isCaseDetailsLoading: PropTypes.bool,
};

CaseOverview.defaultProps = {
  handleDetailClick: () => {},
  isCaseDetailsLoading: false,
};
